/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import { cloneDeep, sortBy } from 'lodash';

/**
 * Combines reducers just like built-in combineReducers except it will ensure
 * producers with a static priority set are called first, in order of their priority.
 *
 * Modified version of combineReducers from commit on Apr 3, 2017.
 * 7e745ce543baecb835173220e7bb51f17b19c875
 * https://github.com/reactjs/redux/blob/master/src/combineReducers.js
 *
 * Reducer sanity checks removed in order to reduce amount of code copied. They're
 * only used in development anyways and catch very basic errors that are rare.
 *
 * @param {Object} reducers An object whose values correspond to different
 * reducer functions that need to be combined into one. Functions can optionally
 * have a priority set on the function object itself.
 *
 * @returns {Function} A reducer function that invokes every reducer inside the
 * passed object, and builds a state object with the same shape.
 *
 *
 */
export function prioritizedCombineReducers(reducers, initialState) {
  const allReducers = createInitialStateConstantReducers(
    reducers,
    initialState
  );

  const keys = sortBy(
    Object.keys(allReducers).filter(
      (k) => typeof allReducers[k] === 'function'
    ),
    (k) => allReducers[k].priority
  ).concat();

  return function combination(state = {}, action) {
    let hasChanged = false;
    const fullStateAsBuilding = cloneDeep(state);
    const nextState = {};
    const keysLength = keys.length;
    for (let i = 0; i < keysLength; i++) {
      const key = keys[i];
      const reducer = allReducers[key];
      const previousStateForKey = state[key];

      let nextStateForKey;

      try {
        nextStateForKey = reducer(
          previousStateForKey,
          action,
          fullStateAsBuilding
        );
      } catch (err) {
        throw new Error(
          `Given action "${
            (action && action.type) || '-no-type-'
          }", reducer "${key}" return throw an error: ${err.toString()}."`
        );
      }

      if (typeof nextStateForKey === 'undefined') {
        throw new Error(
          `Given action "${
            (action && action.type) || '-no-type-'
          }", reducer "${key}" return undefined. Must return previous state."`
        );
      }
      nextState[key] = nextStateForKey;
      fullStateAsBuilding[key] = nextStateForKey;
      hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
    }
    return hasChanged ? nextState : state;
  };
}

/**
 * Creates a reducer that just echos state for each key in initialState that doesn't have a reducer
 * @param reducers
 * @param initialState
 * @returns {Object}
 */
function createInitialStateConstantReducers(reducers, initialState) {
  const allReducers = { ...reducers };

  Object.keys(initialState).forEach((key) => {
    if (allReducers[key] !== undefined) {
      return;
    }

    allReducers[key] = echoReducer;
  });

  return allReducers;
}

function echoReducer(state) {
  return state;
}
